/**
 * 反復測定（縦断）データの分散分析
 *
 * 群（被験体間）× 時点（被験体内）の分割区画デザインとして
 * mixedDesignTwoWayANOVA（因子A=群、因子B=時点）に委譲する。
 * 計算式の詳細は docs/systems/keisuke/FORMULAS.md および anova.ts を参照。
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';
import { mixedDesignTwoWayANOVA } from './anova';

/**
 * 反復測定データのエントリ
 */
export interface RepeatedMeasuresData {
  subjectId: string;
  group: string;
  time: number;
  value: number;
}

/**
 * 反復測定ANOVAの結果
 */
export interface RepeatedMeasuresANOVAResult {
  groupEffect: {
    f: number;
    pValue: number;
    dfBetween: number;
    dfError: number;
    ssBetween: number;
    ssError: number;
    msBetween: number;
    msError: number;
  };
  timeEffect: {
    f: number;
    pValue: number;
    dfBetween: number;
    dfError: number;
    ssBetween: number;
    ssError: number;
    msBetween: number;
    msError: number;
  };
  interaction: {
    f: number;
    pValue: number;
    dfBetween: number;
    dfError: number;
    ssBetween: number;
    ssError: number;
    msBetween: number;
    msError: number;
  };
  groups: Array<{
    name: string;
    n: number;
    means: Array<{
      time: number;
      mean: number;
      n: number;
      /** セル内（同一群・同一時点の被験体間）の標本標準偏差。n<2 のときは未定義 */
      stdDev?: number;
    }>;
  }>;
}

/**
 * 同一 (被験体, 群, 時点) の複数値は平均して1観測にまとめる
 */
function consolidateRows(data: RepeatedMeasuresData[]): RepeatedMeasuresData[] {
  const bucket = new Map<
    string,
    { subjectId: string; group: string; time: number; values: number[] }
  >();
  data.forEach((row) => {
    const key = `${row.subjectId}\t${row.group}\t${row.time}`;
    let b = bucket.get(key);
    if (!b) {
      b = { subjectId: row.subjectId, group: row.group, time: row.time, values: [] };
      bucket.set(key, b);
    }
    b.values.push(row.value);
  });
  return Array.from(bucket.values()).map((b) => ({
    subjectId: b.subjectId,
    group: b.group,
    time: b.time,
    value: b.values.reduce((s, v) => s + v, 0) / b.values.length,
  }));
}

/**
 * 群×時点の反復測定データに対する分割区画 ANOVA（混合デザイン）
 *
 * - 因子A（群）: 被験体間
 * - 因子B（時点）: 被験体内（全被験体で同一時点水準が揃っていること）
 */
export function repeatedMeasuresANOVA(data: RepeatedMeasuresData[]): RepeatedMeasuresANOVAResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  const consolidated = consolidateRows(data);

  const groupNames = Array.from(new Set(consolidated.map((r) => r.group)));
  const timeArray = Array.from(new Set(consolidated.map((r) => r.time))).sort((a, b) => a - b);
  const k = groupNames.length;
  const p = timeArray.length;

  if (k < 2) {
    throw new KeisukeAdapterError('minTwoGroups');
  }
  if (p < 2) {
    throw new KeisukeAdapterError('minTwoTimePoints');
  }

  const mixedRows = consolidated.map((row) => ({
    factorA: row.group,
    factorB: String(row.time),
    subject: row.subjectId,
    value: row.value,
  }));

  const two = mixedDesignTwoWayANOVA(mixedRows);
  const subjectError = two.errorTerms?.[0];
  if (!subjectError) {
    throw new KeisukeAdapterError('mixedDesignErrorTermUnavailable');
  }

  const stdDevForCell = (group: string, time: number): number | undefined => {
    const vals = consolidated
      .filter((r) => r.group === group && r.time === time)
      .map((r) => r.value);
    if (vals.length < 2) return undefined;
    const m = vals.reduce((s, v) => s + v, 0) / vals.length;
    const v = vals.reduce((s, x) => s + (x - m) ** 2, 0) / (vals.length - 1);
    return Math.sqrt(v);
  };

  const groups = two.factorALevels.map((groupName) => {
    const subjectIds = new Set(
      consolidated.filter((r) => r.group === groupName).map((r) => r.subjectId)
    );
    const means = timeArray.map((time) => {
      const cell = two.cells.find((c) => c.factorA === groupName && c.factorB === String(time));
      return {
        time,
        mean: cell?.mean ?? 0,
        n: cell?.n ?? 0,
        stdDev: stdDevForCell(groupName, time),
      };
    });
    return {
      name: groupName,
      n: subjectIds.size,
      means,
    };
  });

  return {
    groupEffect: {
      f: two.factorA.f,
      pValue: two.factorA.pValue,
      dfBetween: two.factorA.df,
      dfError: two.factorA.errorDf ?? subjectError.df,
      ssBetween: two.factorA.ss,
      ssError: subjectError.ss,
      msBetween: two.factorA.ms,
      msError: subjectError.ms,
    },
    timeEffect: {
      f: two.factorB.f,
      pValue: two.factorB.pValue,
      dfBetween: two.factorB.df,
      dfError: two.factorB.errorDf ?? two.within.df,
      ssBetween: two.factorB.ss,
      ssError: two.within.ss,
      msBetween: two.factorB.ms,
      msError: two.within.ms,
    },
    interaction: {
      f: two.interaction.f,
      pValue: two.interaction.pValue,
      dfBetween: two.interaction.df,
      dfError: two.interaction.errorDf ?? two.within.df,
      ssBetween: two.interaction.ss,
      ssError: two.within.ss,
      msBetween: two.interaction.ms,
      msError: two.within.ms,
    },
    groups,
  };
}
